import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CircularProgress from '@mui/material/CircularProgress';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

import TablePageSelect from './tablePageSelect';
import TimeAgo from '../../../hooks/timeConverter';
import TableSearch from './tableComponents/tableSearch';

const EMAIL_STATUS_STYLES = {
    sent: {
        label: 'Sent',
        bgcolor: '#e8f5e9',
        color: '#2e7d32',
    },
    pending: {
        label: 'Pending',
        bgcolor: '#fff3e0',
        color: '#ef6c00',
    },
    sending: {
        label: 'Sending',
        bgcolor: '#fff3e0',
        color: '#ef6c00',
    },
    failed: {
        label: 'Failed',
        bgcolor: '#ffebee',
        color: '#c62828',
    },
};

const StatusChip = ({ status }) => {
    const key = String(status || '').toLowerCase();
    const style = EMAIL_STATUS_STYLES[key] || {
        label: status ? String(status) : '—',
        bgcolor: '#f5f5f5',
        color: '#616161',
    };

    return (
        <Chip
            label={style.label}
            size="small"
            sx={{
                bgcolor: style.bgcolor,
                color: style.color,
                fontWeight: 600,
                borderRadius: '8px',
                textTransform: 'none',
            }}
        />
    );
};

const BooleanChip = ({ value }) => (
    <Chip
        label={value ? 'true' : 'false'}
        size="small"
        sx={{
            bgcolor: value ? '#ffebee' : '#e8f5e9',
            color: value ? '#c62828' : '#2e7d32',
            fontWeight: 600,
            borderRadius: '8px',
            textTransform: 'none',
        }}
    />
);

const headerCellSx = {
    py: 1.5,
    px: 2,
    bgcolor: '#fafafa',
    borderBottom: '1px solid #eee',
    whiteSpace: 'nowrap',
};

const bodyCellSx = {
    py: 1.5,
    px: 2,
    borderBottom: '1px solid #f0f0f0',
    verticalAlign: 'middle',
};

function TableComponent({
    apiData,
    progress,
    noResults,
    newpage,
    columnNames = {},
    handleAdd,
    hiddenColumns = [],
    handleOpen,
    handleDelete,
    handleSpecDelete,
    theme,
    mailingTable,
    query: urlQuery = '',
    pageNumber: urlPage = 1,
}) {
    const [tableData, setTableData] = useState(apiData?.data || []);
    const [page, setPage] = useState(Math.max(1, parseInt(urlPage, 10) || 1));
    const [search, setSearch] = useState(urlQuery || "");

    useEffect(() => {
        setTableData(apiData?.data || []);
    }, [apiData]);

    useEffect(() => {
        setSearch(urlQuery || "");
    }, [urlQuery]);

    useEffect(() => {
        setPage(Math.max(1, parseInt(urlPage, 10) || 1));
    }, [urlPage]);

    const handleSearchRequest = async () => {
        if (!newpage) return;
        setPage(1);
        await newpage(1, search);
    };

    const handleRequest = async (pageNumber) => {
        if (!newpage) return;
        setPage(pageNumber);
        await newpage(pageNumber, search);
    };

    const tableColumns = tableData.length > 0
        ? Object.keys(tableData[0]).filter(
            col =>
                col.toLowerCase() !== 'id' &&
                !hiddenColumns.includes(col)
        )
        : Object.keys(columnNames).filter(
            col =>
                col.toLowerCase() !== 'id' &&
                !hiddenColumns.includes(col)
        );

    const colSpan = Math.max(tableColumns.length + 1, 2);

    const renderCell = (column, row) => {
        if (column === 'status') {
            return <StatusChip status={row[column]} />;
        }

        if (column.includes('time_created') || column.includes('release_time') || column.includes('time_added')) {
            return (
                <Typography variant="body2" color="text.secondary">
                    <TimeAgo timestamp={row[column]} />
                </Typography>
            );
        }

        if (column.includes('icon') && row[column]?.startsWith('data:image')) {
            return (
                <Box
                    component="img"
                    src={row[column]}
                    alt=""
                    sx={{
                        height: 44,
                        width: 44,
                        objectFit: 'cover',
                        borderRadius: '10px',
                        border: '1px solid #eee',
                    }}
                />
            );
        }

        if (typeof row[column] === "boolean") {
            return <BooleanChip value={row[column]} />;
        }

        const value = row[column];

        if (column === 'groups' && Array.isArray(value)) {
            const labels = value
                .map((item) => {
                    if (item == null) return '';
                    if (typeof item === 'string' || typeof item === 'number') {
                        return String(item);
                    }
                    return item.label || item.name || item.list_name || String(item.id || '');
                })
                .filter(Boolean);

            if (labels.length === 0) {
                return (
                    <Typography variant="body2" color="text.secondary">
                        —
                    </Typography>
                );
            }

            const visible = labels.slice(0, 3);
            const remaining = labels.length - visible.length;

            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        alignItems: 'center',
                        maxWidth: 320,
                    }}
                >
                    {visible.map((label, index) => (
                        <Chip
                            key={`${label}-${index}`}
                            label={label}
                            size="small"
                            sx={{
                                bgcolor: '#f5f5f5',
                                color: '#424242',
                                fontWeight: 500,
                                borderRadius: '8px',
                                maxWidth: 140,
                                '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                },
                            }}
                        />
                    ))}
                    {remaining > 0 ? (
                        <Tooltip title={labels.slice(3).join(', ')}>
                            <Chip
                                label={`+${remaining}`}
                                size="small"
                                sx={{
                                    bgcolor: '#eeeeee',
                                    color: '#616161',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                }}
                            />
                        </Tooltip>
                    ) : null}
                </Box>
            );
        }

        const display =
            value === null || value === undefined || value === ''
                ? '—'
                : Array.isArray(value)
                    ? value.join(', ')
                    : String(value);

        return (
            <Typography
                variant="body2"
                sx={{
                    color: 'text.primary',
                    fontWeight: 500,
                    maxWidth: 280,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
                title={display !== '—' ? display : undefined}
            >
                {display}
            </Typography>
        );
    };

    return (
        <Box>
            <TableSearch
                search={search}
                setSearch={setSearch}
                newPage={handleSearchRequest}
                handleAdd={handleAdd}
                theme={theme}
                disabled={!newpage}
            />

            <TableContainer
                sx={{
                    border: '1px solid #eee',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                }}
            >
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            {tableColumns.map((column) => (
                                <TableCell key={column} align="left" sx={headerCellSx}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.2 }}
                                    >
                                        {columnNames[column] || column.replace(/_/g, ' ')}
                                    </Typography>
                                </TableCell>
                            ))}
                            <TableCell align="right" sx={headerCellSx}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.2 }}
                                >
                                    Actions
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {progress ? (
                            <TableRow>
                                <TableCell colSpan={colSpan} align="center" sx={{ border: 0 }}>
                                    <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                        <CircularProgress color="primary" size={36} />
                                        <Typography variant="body2" color="text.secondary">
                                            Loading results...
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : noResults || tableData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={colSpan} align="center" sx={{ border: 0 }}>
                                    <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
                                        <Box
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: '50%',
                                                bgcolor: '#fafafa',
                                                border: '1px solid #eee',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1.5,
                                            }}
                                        >
                                            <InboxOutlinedIcon sx={{ color: 'text.secondary' }} />
                                        </Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            No results found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Try adjusting your search or add a new item.
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            tableData.map((row) => (
                                <TableRow
                                    key={row.id}
                                    hover
                                    sx={{
                                        '&:last-child td': { borderBottom: 0 },
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    {tableColumns.map((column) => (
                                        <TableCell key={column} align="left" sx={bodyCellSx}>
                                            {renderCell(column, row)}
                                        </TableCell>
                                    ))}
                                    <TableCell align="right" sx={bodyCellSx}>
                                        <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                                            {handleOpen ? (
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            const value = mailingTable ? row : row.id;
                                                            handleOpen(value);
                                                        }}
                                                        sx={{
                                                            color: 'text.secondary',
                                                            border: '1px solid #eee',
                                                            borderRadius: '8px',
                                                            '&:hover': {
                                                                color: 'primary.main',
                                                                bgcolor: 'rgba(255,0,0,0.04)',
                                                                borderColor: 'primary.light',
                                                            },
                                                        }}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                            {handleDelete ? (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(row)}
                                                        sx={{
                                                            color: 'text.secondary',
                                                            border: '1px solid #eee',
                                                            borderRadius: '8px',
                                                            '&:hover': {
                                                                color: 'error.main',
                                                                bgcolor: 'rgba(198,40,40,0.04)',
                                                                borderColor: 'error.light',
                                                            },
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                            {handleSpecDelete ? (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleSpecDelete(row.id)}
                                                        sx={{
                                                            color: 'text.secondary',
                                                            border: '1px solid #eee',
                                                            borderRadius: '8px',
                                                            '&:hover': {
                                                                color: 'error.main',
                                                                bgcolor: 'rgba(198,40,40,0.04)',
                                                                borderColor: 'error.light',
                                                            },
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {newpage ? (
                <TablePageSelect newpage={handleRequest} data={apiData} page={page} setPage={setPage} />
            ) : null}
        </Box>
    );
}

export default TableComponent;
